import { Injectable } from '@angular/core';
import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { from, Observable, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';

@Injectable()
export class AuthTokenInterceptor implements HttpInterceptor {
  constructor(private authService: AuthService) {}

  // Ensures API requests carry auth tokens only when a session exists.
  intercept(
    req: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    // Skip external URLs (e.g., third-party APIs)
    if (this.isExternalUrl(req.url)) {
      return next.handle(req);
    }

    const path = this.resolvePath(req.url);
    const isApiRequest = path.startsWith('/api/');
    if (!isApiRequest) {
      return next.handle(req);
    }

    const requiresCredentials = !req.withCredentials;

    const skipReason = this.shouldSkipAuthorization(req, path);
    if (skipReason) {
      // Allow unauthenticated endpoints to pass through unchanged.
      if (skipReason === 'config') {
        return next.handle(req);
      }

      // Refresh and login endpoints still require cookies for the backend.
      const request = requiresCredentials
        ? req.clone({ withCredentials: true })
        : req;
      return next.handle(request);
    }

    const hasValidToken = this.authService.hasValidAccessToken();
    const canRefresh = this.authService.canAttemptSilentRefresh();

    const token$ = hasValidToken
      ? of(this.authService.getAccessToken())
      : canRefresh
        ? from(this.authService.awaitAccessToken())
        : of(null);

    return token$.pipe(
      switchMap((token) => {
        const cloneOptions: {
          setHeaders?: Record<string, string>;
          withCredentials?: boolean;
        } = {};

        if (token) {
          cloneOptions.setHeaders = {
            Authorization: `Bearer ${token}`,
          };
        }

        if (requiresCredentials) {
          cloneOptions.withCredentials = true;
        }

        const authenticatedRequest =
          cloneOptions.setHeaders || cloneOptions.withCredentials
            ? req.clone(cloneOptions)
            : req;

        return next.handle(authenticatedRequest);
      })
    );
  }

  private shouldSkipAuthorization(
    req: HttpRequest<unknown>,
    path: string
  ): 'refresh' | 'login' | 'microsoft' | 'config' | null {
    const normalized = path.split('?')[0];
    if (normalized === '/api/auth/refresh') {
      return 'refresh';
    }

    if (normalized === '/api/auth' && req.method.toUpperCase() === 'POST') {
      return 'login';
    }

    if (
      normalized === '/api/auth/microsoft' &&
      req.method.toUpperCase() === 'POST'
    ) {
      return 'microsoft';
    }

    if (
      normalized === '/api/auth/microsoft/config' &&
      req.method.toUpperCase() === 'GET'
    ) {
      return 'config';
    }

    return null;
  }

  private isExternalUrl(url: string): boolean {
    return url.startsWith('http://') || url.startsWith('https://');
  }

  private resolvePath(url: string): string {
    if (this.isExternalUrl(url)) {
      const pathIndex = url.indexOf('/', url.indexOf('//') + 2);
      return pathIndex >= 0 ? url.substring(pathIndex) : '/';
    }

    return url;
  }
}
